import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, X, AlertCircle, User, Heart, Home, Briefcase, Users, Brain, DollarSign, Activity, ChevronDown, ChevronUp } from 'lucide-react';
import { prepareProfileEnrichment, getEnrichmentSummary } from '../../services/profileEnrichmentService';
import './ProfileEnrichmentModal.css';

/**
 * Modal d'aperçu pour l'enrichissement du profil
 * Affiche un aperçu détaillé des données avant/après synchronisation
 */
const ProfileEnrichmentModal = ({
  assessmentData,
  beneficiary,
  onConfirm,
  onCancel
}) => {
  const [enrichmentData, setEnrichmentData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState({
    contacts: true,
    health: true,
    autonomy: true,
    services: true,
    housing: true,
    caregiver: true,
    psychological: true,
    aids: true
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    // Préparer les données d'enrichissement
    const data = prepareProfileEnrichment(assessmentData);
    setEnrichmentData(data);

    // Calculer le résumé
    const summaryData = getEnrichmentSummary(data);
    setSummary(summaryData);
  }, [assessmentData]);

  const handleCategoryToggle = (category) => {
    setSelectedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleToggleExpand = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleSelectAll = () => {
    const allSelected = Object.values(selectedCategories).every(v => v);
    const newState = {};
    Object.keys(selectedCategories).forEach(key => {
      newState[key] = !allSelected;
    });
    setSelectedCategories(newState);
  };

  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      // Filtrer les données selon les catégories sélectionnées
      const fieldsToUpdate = [];

      if (selectedCategories.contacts) {
        fieldsToUpdate.push('emergencyContacts', 'trustedPerson');
      }
      if (selectedCategories.health) {
        fieldsToUpdate.push('doctor', 'healthSummary');
      }
      if (selectedCategories.autonomy) {
        fieldsToUpdate.push('autonomyAssessment');
      }
      if (selectedCategories.services) {
        fieldsToUpdate.push('serviceNeeds');
      }
      if (selectedCategories.housing) {
        fieldsToUpdate.push('housingSituation');
      }
      if (selectedCategories.caregiver) {
        fieldsToUpdate.push('familyCaregiver');
      }
      if (selectedCategories.psychological) {
        fieldsToUpdate.push('psychologicalStatus');
      }
      if (selectedCategories.aids) {
        fieldsToUpdate.push('currentAids');
      }

      // Toujours mettre à jour les métadonnées
      fieldsToUpdate.push('lastQuestionnaireUpdate', 'questionnaireCompletionDate');

      await onConfirm(enrichmentData, fieldsToUpdate);
    } catch (error) {
      console.error('Erreur lors de l\'enrichissement:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      contacts: User,
      health: Heart,
      autonomy: Activity,
      services: Briefcase,
      housing: Home,
      caregiver: Users,
      psychological: Brain,
      aids: DollarSign
    };
    return icons[category] || AlertCircle;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      contacts: 'Contacts et urgences',
      health: 'Santé et professionnels',
      autonomy: 'Autonomie et GIR',
      services: 'Besoins en services',
      housing: 'Logement et environnement',
      caregiver: 'Aidant familial',
      psychological: 'Situation psychologique',
      aids: 'Aides financières'
    };
    return labels[category] || category;
  };

  const getCategoryDescription = (category) => {
    if (!enrichmentData) return '';

    switch (category) {
      case 'contacts':
        return `${enrichmentData.emergencyContacts?.length || 0} contact(s) d'urgence, personne de confiance`;
      case 'health':
        return `Médecin traitant, pathologies, ${enrichmentData.healthSummary?.medicationCount || 0} médicaments`;
      case 'autonomy':
        return `Score: ${enrichmentData.autonomyAssessment?.autonomyScore || 'N/A'}%, GIR: ${enrichmentData.autonomyAssessment?.gir || 'Non renseigné'}`;
      case 'services':
        const totalServices = (enrichmentData.serviceNeeds?.personalCare?.length || 0) +
                             (enrichmentData.serviceNeeds?.housekeeping?.length || 0) +
                             (enrichmentData.serviceNeeds?.accompaniment?.length || 0);
        return `${totalServices} service(s), ${enrichmentData.serviceNeeds?.estimatedWeeklyHours || 0}h/semaine`;
      case 'housing':
        return `${enrichmentData.housingSituation?.adaptations?.length || 0} adaptation(s), ${enrichmentData.housingSituation?.hasRisks ? 'risques identifiés' : 'pas de risque'}`;
      case 'caregiver':
        return enrichmentData.familyCaregiver ? `${enrichmentData.familyCaregiver.name || 'Aidant'} - ${enrichmentData.familyCaregiver.relationship || ''}` : 'Aucun aidant';
      case 'psychological':
        return `État: ${enrichmentData.psychologicalStatus?.mood || 'Non renseigné'}, ${enrichmentData.psychologicalStatus?.activities?.length || 0} activité(s)`;
      case 'aids':
        const hasAPA = enrichmentData.currentAids?.apa?.has;
        const hasPCH = enrichmentData.currentAids?.pch?.has;
        return hasAPA ? 'APA (oui)' : hasPCH ? 'PCH (oui)' : 'Aucune aide déclarée';
      default:
        return '';
    }
  };

  const renderCategoryDetails = (category) => {
    if (!enrichmentData || !expandedCategories[category]) return null;

    switch (category) {
      case 'contacts':
        return (
          <div className="enrichment-details">
            <h5>Contacts d'urgence:</h5>
            {enrichmentData.emergencyContacts?.map((contact, index) => (
              <div key={index} className="detail-item">
                <strong>{contact.name}</strong> ({contact.relationship}) - {contact.phone1}
              </div>
            ))}
            {enrichmentData.trustedPerson && (
              <>
                <h5>Personne de confiance:</h5>
                <div className="detail-item">
                  <strong>{enrichmentData.trustedPerson.name}</strong> ({enrichmentData.trustedPerson.relationship}) - {enrichmentData.trustedPerson.phone}
                </div>
              </>
            )}
          </div>
        );

      case 'health':
        return (
          <div className="enrichment-details">
            {enrichmentData.doctor && (
              <>
                <h5>Médecin traitant:</h5>
                <div className="detail-item">
                  <strong>{enrichmentData.doctor.name}</strong> - {enrichmentData.doctor.phone}
                </div>
              </>
            )}
            {enrichmentData.healthSummary?.mainPathologies && (
              <>
                <h5>Pathologies principales:</h5>
                <div className="detail-item">{enrichmentData.healthSummary.mainPathologies}</div>
              </>
            )}
            {enrichmentData.healthSummary?.medicationCount && (
              <div className="detail-item">
                <strong>{enrichmentData.healthSummary.medicationCount}</strong> médicament(s) régulier(s)
              </div>
            )}
          </div>
        );

      case 'autonomy':
        return (
          <div className="enrichment-details">
            <h5>Évaluation d'autonomie:</h5>
            <div className="detail-item">
              <strong>Score:</strong> {enrichmentData.autonomyAssessment?.autonomyScore}%
            </div>
            <div className="detail-item">
              <strong>GIR:</strong> {enrichmentData.autonomyAssessment?.gir || 'Non renseigné'}
            </div>
            {enrichmentData.autonomyAssessment?.needsHelp && (
              <div className="detail-item">
                <strong>Domaines nécessitant de l'aide:</strong>
                <ul>
                  {Object.entries(enrichmentData.autonomyAssessment.needsHelp)
                    .filter(([_, needsHelp]) => needsHelp)
                    .map(([domain]) => (
                      <li key={domain}>{domain}</li>
                    ))}
                </ul>
              </div>
            )}
          </div>
        );

      case 'services':
        return (
          <div className="enrichment-details">
            <h5>Besoins en services:</h5>
            {enrichmentData.serviceNeeds?.personalCare?.length > 0 && (
              <div className="detail-item">
                <strong>Soins personnels:</strong> {enrichmentData.serviceNeeds.personalCare.join(', ')}
              </div>
            )}
            {enrichmentData.serviceNeeds?.housekeeping?.length > 0 && (
              <div className="detail-item">
                <strong>Entretien du logement:</strong> {enrichmentData.serviceNeeds.housekeeping.join(', ')}
              </div>
            )}
            {enrichmentData.serviceNeeds?.estimatedWeeklyHours && (
              <div className="detail-item">
                <strong>Heures estimées:</strong> {enrichmentData.serviceNeeds.estimatedWeeklyHours}h/semaine
              </div>
            )}
          </div>
        );

      case 'housing':
        return (
          <div className="enrichment-details">
            <h5>Situation du logement:</h5>
            <div className="detail-item">
              <strong>Type:</strong> {enrichmentData.housingSituation?.type}
            </div>
            {enrichmentData.housingSituation?.adaptations?.length > 0 && (
              <div className="detail-item">
                <strong>Adaptations:</strong> {enrichmentData.housingSituation.adaptations.join(', ')}
              </div>
            )}
            {enrichmentData.housingSituation?.hasRisks && (
              <div className="detail-item">
                <strong>Risques identifiés:</strong> {enrichmentData.housingSituation.risksDescription}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const selectedCount = Object.values(selectedCategories).filter(v => v).length;

  if (!enrichmentData || !summary) {
    return createPortal(
      <div className="enrichment-modal-overlay" style={{ zIndex: 10000 }}>
        <div className="enrichment-modal">
          <p>Chargement des données d'enrichissement...</p>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="enrichment-modal-overlay" style={{ zIndex: 10000 }}>
      <div className="enrichment-modal">
        <div className="enrichment-modal-header">
          <div>
            <h2>Enrichir le profil de {beneficiary?.firstName} {beneficiary?.lastName}</h2>
            <p>Sélectionnez les informations à synchroniser avec le profil</p>
          </div>
          <button onClick={onCancel} className="enrichment-close-button">
            <X size={24} />
          </button>
        </div>

        <div className="enrichment-modal-body">
          <div className="enrichment-info-banner">
            <AlertCircle size={20} />
            <p>Le questionnaire d'évaluation contient de nombreuses informations utiles. Vous pouvez choisir lesquelles ajouter au profil du bénéficiaire.</p>
          </div>

          <div className="enrichment-actions">
            <button onClick={handleSelectAll} className="enrichment-toggle-all">
              {Object.values(selectedCategories).every(v => v) ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>
            <span className="enrichment-selected-count">
              {selectedCount} / {Object.keys(selectedCategories).length} catégories sélectionnées
            </span>
          </div>

          <div className="enrichment-categories">
            {Object.keys(selectedCategories).map(category => {
              const Icon = getCategoryIcon(category);
              const hasData = summary[category] > 0;
              const isExpanded = expandedCategories[category];

              return (
                <div
                  key={category}
                  className={`enrichment-category ${selectedCategories[category] ? 'selected' : ''} ${!hasData ? 'disabled' : ''}`}
                >
                  <div className="enrichment-category-header">
                    <input
                      type="checkbox"
                      checked={selectedCategories[category]}
                      onChange={() => hasData && handleCategoryToggle(category)}
                      disabled={!hasData}
                    />
                    <Icon size={20} className="enrichment-category-icon" />
                    <span className="enrichment-category-label">{getCategoryLabel(category)}</span>
                    {!hasData && (
                      <span className="enrichment-no-data">Aucune donnée</span>
                    )}
                    {hasData && (
                      <button
                        onClick={() => handleToggleExpand(category)}
                        className="enrichment-expand-button"
                      >
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                    )}
                  </div>
                  
                  {hasData && (
                    <>
                      <p className="enrichment-category-description">
                        {getCategoryDescription(category)}
                      </p>
                      {renderCategoryDetails(category)}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="enrichment-modal-footer">
          <button
            onClick={onCancel}
            className="enrichment-btn enrichment-btn-secondary"
            disabled={isProcessing}
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            className="enrichment-btn enrichment-btn-primary"
            disabled={isProcessing || selectedCount === 0}
          >
            {isProcessing ? (
              'Mise à jour en cours...'
            ) : (
              <>
                <CheckCircle size={18} />
                Mettre à jour le profil ({selectedCount})
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProfileEnrichmentModal;
